import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import "./working.css";

import connectWallet from "../../../../static/Images/working/connect_wallet.png";
import selectGame from "../../../../static/Images/working/select_game.png";
import placeBet from "../../../../static/Images/working/place_bet.png";
import result from "../../../../static/Images/working/result.png";
import withdraw from "../../../../static/Images/working/withdraw.png";

import { setupWorkingAnimations } from "../animations/workAnimation";

import colors from "../../../../theme/colors";
import typography from "../../../../theme/typography";

const steps = [
  {
    number: "01",
    title: "Connect Wallet",
    description:
      "Connect your crypto wallet securely to AutoBET and get access to the platform. Your wallet is used to manage bets and winnings.",
    image: connectWallet,
  },
  {
    number: "02",
    title: "Choose a Game",
    description:
      "Explore the available games and choose the one you want to play. Each game offers a simple and transparent betting experience.",
    image: selectGame,
  },
  {
    number: "03",
    title: "Place Your Bet",
    description:
      "Select your preferred bet amount and make your choice. Review the details and confirm your bet to enter the game.",
    image: placeBet,
  },
  {
    number: "04",
    title: "Results on-Chain",
    description:
      "Once the game is completed, the result is recorded on-chain. This makes the outcome transparent, verifiable, and easy to trust.",
    image: result,
  },
  {
    number: "05",
    title: "Win & Withdraw",
    description:
      "If you win, your rewards become available to withdraw. Transfer your winnings securely back to your connected wallet.",
    image: withdraw,
  },
];

const Working = () => {
  const theme = useTheme();
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const detailRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);

  const activeStepData = steps[activeStep];

  useEffect(() => {
    const cleanup = setupWorkingAnimations(sectionRef.current, setActiveStep);

    return cleanup;
  }, []);

  const handleStepChange = (index) => {
    if (index === activeStep) return;

    setActiveStep(index);
  };

  const isBlue = activeStep % 2 === 1;
  const activeColor = isBlue ? (colors?.primary?.main || "#315bff") : (colors?.home?.brandRed || "#ff3347");
  const activeColorAlpha = isBlue ? "rgba(49, 91, 255, 0.46)" : "rgba(255, 113, 130, 0.46)";
  const activeShadow = isBlue ? "rgba(49, 91, 255, 0.22)" : "rgba(255, 51, 71, 0.22)";
  
  const activeMobileBg = isBlue ? "#f0f4ff" : "#fff4f6";
  const activeMobileNum = isBlue ? "rgba(49, 91, 255, 0.12)" : "rgba(255, 51, 71, 0.12)";

  return (
    <Box sx={{ background: "#ffffff", position: "relative", zIndex: 2, width: "100%" }}>
      <Box
        ref={sectionRef}
        component="section"
        className="working-section"
        sx={{
          "--working-primary": colors?.home?.brandRed || "#ff3347",
          "--working-secondary": colors?.home?.grayBlue || "#315bff",
          "--working-active": activeColor,
          "--working-active-alpha": activeColorAlpha,
          "--working-active-shadow": activeShadow,
          "--working-mobile-bg": activeMobileBg,
          "--working-mobile-num": activeMobileNum,
          "--working-dark": colors?.home?.navyDeep || "#0d1526",
          "--working-muted": colors?.home?.textMuted || "#68778f",
          "--working-font": typography?.fontFamily || "Inter, sans-serif",
        }}
      >
      {/* Background decoration */}

      <Box
        className="working-decoration working-decoration-one"
        aria-hidden="true"
      />

      <Box
        className="working-decoration working-decoration-two"
        aria-hidden="true"
      />

      <Container 
        maxWidth={false}
        className="working-container"
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          px: theme.custom["home-section-left-padding"],
          pt: theme.custom.layoutSizes.section.paddingTop,
          pb: theme.custom.layoutSizes.section.paddingBottom,
        }}
      >

        {/* =========================================
            HEADER
        ========================================= */}

        <Box 
          className="working-header"
          sx={(theme) => ({
            ...(theme.typography.homeSection?.headerContainer || {}),
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            mb: (theme) => theme.typography.homeSection?.headerBottomMargin || { xs: 4, md: 5 },
          })}
        >

          <Typography
            component="p"
            className="working-eyebrow"
            sx={(theme) => ({
              ...(theme.typography.homeSection?.eyebrow || {}),
              color: theme.custom.colors.home.brandRed,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              "&::before": {
                ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                background: theme.custom.colors.home.brandRed,
              },
            })}
          >
            HOW IT WORKS
          </Typography>

          <Typography
            component="h2"
            className="working-main-title"
            sx={(theme) => ({
              ...(theme.typography.homeSection?.heading || {}),
              color: theme.custom.colors.home.navyDark,
              textAlign: "left",
            })}
          >
            Simple. Secure. Transparent.
          </Typography>

          <Typography 
            className="working-subtitle"
            sx={(theme) => ({
              ...(theme.typography.homeSection?.description || {}),
              color: "text.secondary",
              textAlign: "left",
              maxWidth: "900px",
            })}
          >
            Get started in just a few easy steps and join thousands
            of players worldwide.
          </Typography>

        </Box>

        {/* =========================================
            MAIN AREA
        ========================================= */}

        <Box className="working-main">

          {/* =====================================
              LEFT IMAGE
          ===================================== */}

          <Box className="working-visual">

            <Box className="working-image-card">

              <Typography className="working-image-number">
                {activeStepData.number}
              </Typography>

              <Box
                ref={imageRef}
                className="working-image-wrapper"
              >
                {steps.map((step, index) => (
                  <Box
                    key={step.number}
                    component="img"
                    src={step.image}
                    alt={step.title}
                    className={`working-image ${index === activeStep ? 'active' : ''}`}
                  />
                ))}
              </Box>

            </Box>

          </Box>

          {/* =====================================
              RIGHT CONTENT
          ===================================== */}

          <Box className="working-info">

            {/* STEP NAVIGATION */}

            <Box className="working-step-navigation">

              {steps.map((step, index) => (
                <React.Fragment key={step.number}>

                  <Button
                    type="button"
                    disableRipple
                    className={`working-step ${
                      index === activeStep ? "active" : ""
                    }`}
                  >
                    <Box className="working-step-circle">
                      <Typography component="span">
                        {step.number}
                      </Typography>
                    </Box>

                    <Typography className="working-step-title">
                      {step.title}
                    </Typography>
                  </Button>

                  {index < steps.length - 1 && (
                    <Box className="working-step-line" />
                  )}

                </React.Fragment>
              ))}

            </Box>

            {/* DETAIL */}

            <Box
              ref={detailRef}
              className="working-detail"
            >

              <Typography className="working-detail-label">
                STEP {activeStepData.number}
              </Typography>

              <Typography
                component="h3"
                className="working-detail-title"
              >
                {activeStepData.title}
              </Typography>

              <Typography className="working-detail-description">
                {activeStepData.description}
              </Typography>

              {/* ACTION */}

              <Box className="working-actions">

                <Box className="working-security">

                  <Box className="working-security-icon">
                    <Typography component="span">
                      ✓
                    </Typography>
                  </Box>

                  <Typography>
                    Secure & transparent
                  </Typography>

                </Box>

              </Box>

            </Box>

          </Box>

        </Box>



      </Container>
      </Box>
    </Box>
  );
};

export default Working;